// Supply WorkOrderOperationsPanels (Phase 4 OM 2026-06-03) — EN + TR.
//
// 3 panels gated by WO state:
//   in_production           → QC + Dispatch trigger
//   dispatched_to_warehouse → Scan (Aybars NJ receive)
//
// S8 invariant: NO seller/customer wording.

import type { LocalizedDict } from '../index'

type WorkOrderOperationsStrings = {
  qc: {
    title: string
    reworkAttempts: (n: number) => string
    disputeRequired: string
    unitsInspectedLabel: string
    unitsPassedLabel: string
    unitsFailedLabel: string
    outcomeLabel: string
    outcomePassed: string
    outcomeRework: string
    reworkReasonLabel: string
    reworkReasonPlaceholder: string
    submitButton: string
    submitting: string
    passSuccessTitle: string
    passSuccessDesc: (passed: number, inspected: number) => string
    reworkSuccessTitle: string
    reworkSuccessDesc: (attempt: number) => string
    errorTitle: string
    eventLogLabel: string
    eventPassedTag: string
    eventReworkTag: string
  }
  dispatch: {
    title: string
    modeHub: string
    modeDropship: string
    subtitleHub: string
    subtitleDropship: string
    dispatchButton: string
    requiresQcNote: string
    dialogTitle: string
    dialogDescHub: string
    dialogDescDropship: string
    carrierLabel: string
    carrierPlaceholder: string
    requiredHint: string
    trackingLabel: string
    trackingPlaceholder: string
    noteLabel: string
    notePlaceholder: string
    cancel: string
    dispatching: string
    dispatchSuccessTitle: string
    dispatchSuccessDesc: (carrier: string, tracking: string) => string
    dispatchErrorTitle: string
    summaryTitle: string
    summaryItem: (productName: string, quantity: number) => string
    summaryStatus: (from: string, to: string) => string
    summaryFinal: string
    confirmButton: string
    qcNotPassedError: string
  }
  scan: {
    title: string
    subtitle: string
    dispatchedAt: string
    carrierTracking: string
    scanTokenLabel: string
    scanTokenPlaceholder: string
    scanTokenNote: string
    noteLabel: string
    notePlaceholder: string
    submitButton: string
    submitting: string
    successTitle: string
    successDesc: (productName: string) => string
    errorTitle: string
  }
}

export const workOrderOperationsI18n: LocalizedDict<WorkOrderOperationsStrings> = {
  en: {
    qc: {
      title: 'Quality control',
      reworkAttempts: (n) => `Rework attempts: ${n}/3`,
      disputeRequired:
        'Rework cap reached. WO escalated to admin (dispute_required). Further QC is locked.',
      unitsInspectedLabel: 'Units inspected',
      unitsPassedLabel: 'Units passed',
      unitsFailedLabel: 'Units failed',
      outcomeLabel: 'Outcome',
      outcomePassed: 'Passed',
      outcomeRework: 'Rework',
      reworkReasonLabel: 'Rework reason',
      reworkReasonPlaceholder: 'e.g. Galvanizing finish uneven on 2 units',
      submitButton: 'Submit QC outcome',
      submitting: 'Submitting…',
      passSuccessTitle: 'QC passed',
      passSuccessDesc: (passed, inspected) =>
        `${passed}/${inspected} units passed; WO ready for dispatch.`,
      reworkSuccessTitle: 'Rework attempt recorded',
      reworkSuccessDesc: (attempt) => `Attempt ${attempt}/3 logged.`,
      errorTitle: 'QC submission failed',
      eventLogLabel: 'QC event log',
      eventPassedTag: 'Passed',
      eventReworkTag: 'Rework',
    },
    dispatch: {
      title: 'Dispatch',
      modeHub: 'Via our NJ warehouse',
      modeDropship: 'Straight to the customer',
      subtitleHub:
        'Ship the produced units to the PEXIFY NJ warehouse. Aybars will scan-in on arrival.',
      subtitleDropship:
        'Ship the produced units directly to the customer. NJ warehouse is skipped on this path.',
      dispatchButton: 'Dispatch',
      requiresQcNote:
        'Record a passed quality check above first. Dispatch opens after that.',
      dialogTitle: 'Confirm dispatch',
      dialogDescHub:
        'The parcel goes to the PEXIFY warehouse in New Jersey. The team there checks it in when it arrives.',
      dialogDescDropship:
        "The parcel goes straight to the customer's address.",
      carrierLabel: 'Carrier',
      carrierPlaceholder: 'Choose a carrier',
      requiredHint: 'Choose a carrier and enter the tracking number to dispatch.',
      trackingLabel: 'Tracking number',
      trackingPlaceholder: 'e.g. 1Z999AA10123456789',
      noteLabel: 'Internal note (optional)',
      notePlaceholder: 'Anything the NJ team should know on arrival',
      cancel: 'Cancel',
      dispatching: 'Dispatching…',
      dispatchSuccessTitle: 'Dispatch recorded',
      dispatchSuccessDesc: (carrier, tracking) =>
        `${carrier} ${tracking} · admin order timeline cascaded.`,
      dispatchErrorTitle: 'Dispatch failed',
      summaryTitle: 'What happens',
      summaryItem: (name, qty) => `${qty} × ${name} leave your workshop.`,
      summaryStatus: (from, to) => `Status changes from “${from}” to “${to}”.`,
      summaryFinal: 'You cannot undo this step yourself. Check the carrier and tracking number.',
      confirmButton: 'Confirm dispatch',
      qcNotPassedError:
        'Quality check has not passed yet. Record a passed quality check, then dispatch again.',
    },
    scan: {
      title: 'NJ receive scan',
      subtitle:
        'Scan the WO QR token on arrival. State will advance to received_at_warehouse and the admin order moves to packaging.',
      dispatchedAt: 'Dispatched at',
      carrierTracking: 'Carrier + tracking',
      scanTokenLabel: 'Scan token',
      scanTokenPlaceholder: 'WO-XXXXX-XXXX',
      scanTokenNote:
        'Scan the QR on the packing slip. V1 mock: validation is pattern-only.',
      noteLabel: 'Receive note (optional)',
      notePlaceholder: 'Damage on outer box, missing units, etc.',
      submitButton: 'Confirm receive',
      submitting: 'Confirming…',
      successTitle: 'WO received at NJ',
      successDesc: (name) => `${name} confirmed in warehouse; admin order → packaging.`,
      errorTitle: 'Receive scan failed',
    },
  },
  tr: {
    qc: {
      title: 'Kalite kontrol',
      reworkAttempts: (n) => `Yeniden işleme denemesi: ${n}/3`,
      disputeRequired:
        'Yeniden işleme sınırı doldu. WO admin`e tırmandırıldı (dispute_required). QC kilitli.',
      unitsInspectedLabel: 'İncelenen adet',
      unitsPassedLabel: 'Geçen adet',
      unitsFailedLabel: 'Geçmeyen adet',
      outcomeLabel: 'Sonuç',
      outcomePassed: 'Geçti',
      outcomeRework: 'Yeniden işleme',
      reworkReasonLabel: 'Yeniden işleme sebebi',
      reworkReasonPlaceholder: 'Örn: Galvaniz kaplama 2 adette yetersiz',
      submitButton: 'QC sonucunu kaydet',
      submitting: 'Kaydediliyor…',
      passSuccessTitle: 'QC geçti',
      passSuccessDesc: (passed, inspected) =>
        `${passed}/${inspected} adet geçti; WO gönderim için hazır.`,
      reworkSuccessTitle: 'Yeniden işleme denemesi kaydedildi',
      reworkSuccessDesc: (attempt) => `${attempt}/3 deneme kaydedildi`,
      errorTitle: 'QC kaydı başarısız',
      eventLogLabel: 'QC olay kaydı',
      eventPassedTag: 'Geçti',
      eventReworkTag: 'Yeniden işleme',
    },
    dispatch: {
      title: 'Gönderim',
      modeHub: 'NJ depomuz üzerinden',
      modeDropship: 'Doğrudan müşteriye',
      subtitleHub:
        'Üretilen adetleri PEXIFY NJ deposuna gönder. Aybars varışta QR ile tarama yapacak.',
      subtitleDropship:
        'Üretilen adetleri doğrudan müşteriye gönder. NJ deposu bu yolda atlanır.',
      dispatchButton: 'Gönderime ver',
      requiresQcNote:
        'Önce yukarıda kalite kontrolünü "Geçti" olarak kaydedin. Gönderim ondan sonra açılır.',
      dialogTitle: 'Gönderimi onayla',
      dialogDescHub:
        "Paket New Jersey'deki PEXIFY deposuna gider. Oradaki ekip paket gelince teslim alır.",
      dialogDescDropship:
        'Paket doğrudan müşterinin adresine gider.',
      carrierLabel: 'Kargo şirketi',
      carrierPlaceholder: 'Kargo şirketi seçin',
      requiredHint: 'Göndermek için kargo şirketini seçin ve takip numarasını girin.',
      trackingLabel: 'Takip numarası',
      trackingPlaceholder: 'Örn: 1Z999AA10123456789',
      noteLabel: 'Dahili not (opsiyonel)',
      notePlaceholder: 'NJ ekibinin bilmesi gereken bir şey',
      cancel: 'Vazgeç',
      dispatching: 'Gönderiliyor…',
      dispatchSuccessTitle: 'Gönderim kaydedildi',
      dispatchSuccessDesc: (carrier, tracking) =>
        `${carrier} ${tracking} · admin sipariş zaman çizelgesi güncellendi.`,
      dispatchErrorTitle: 'Gönderim başarısız',
      summaryTitle: 'Ne olacak',
      summaryItem: (name, qty) => `${qty} adet ${name} atölyenizden çıkar.`,
      summaryStatus: (from, to) => `Durum “${from}” iken “${to}” olur.`,
      summaryFinal: 'Bu adımı kendiniz geri alamazsınız. Kargo şirketini ve takip numarasını kontrol edin.',
      confirmButton: 'Gönderimi onayla',
      qcNotPassedError:
        'Kalite kontrol henüz geçmedi. Önce kalite kontrolünü "Geçti" olarak kaydedin, sonra tekrar gönderin.',
    },
    scan: {
      title: 'NJ teslim taraması',
      subtitle:
        "WO QR token'ını varışta tara. Durum received_at_warehouse'a ilerler ve admin sipariş packaging'e geçer.",
      dispatchedAt: 'Gönderim tarihi',
      carrierTracking: 'Kargo + takip',
      scanTokenLabel: 'Tarama kodu',
      scanTokenPlaceholder: 'WO-XXXXX-XXXX',
      scanTokenNote:
        'Paketleme fişindeki QR`yi tara. V1 mock: sadece biçim doğrulanır.',
      noteLabel: 'Teslim notu (opsiyonel)',
      notePlaceholder: 'Dış kutuda hasar, eksik adet, vb.',
      submitButton: 'Teslimı onayla',
      submitting: 'Onaylanıyor…',
      successTitle: 'WO NJ`de teslim alındı',
      successDesc: (name) => `${name} depoda; admin sipariş → packaging.`,
      errorTitle: 'Teslim taraması başarısız',
    },
  },
}
